/**
 * klinecharts custom-indicator registrations.
 *
 * We bring the same 10 indicator types we had on lightweight-charts
 * (SMA, EMA, WMA, Bollinger, Donchian, RSI, MACD, Stochastic, ATR, ROC)
 * and wire them into klinecharts' IndicatorTemplate API. Each template's
 * `calc(dataList, indicator)` receives the bar list and the live `calcParams`
 * the user set when calling `chart.createIndicator(name, options)`, so a
 * single registration covers any parameter combination (no need to name a
 * fresh indicator per period value).
 *
 * The compute math is verbatim from the old lib/indicators.ts (which is now
 * only kept for the IndicatorInstance UI model + $-reference resolution).
 *
 * Indicator series semantics in klinecharts:
 *   - "price": overlay on the candle pane (SMA, EMA, WMA, Bollinger, Donchian)
 *   - "normal": its own pane below (RSI, MACD, Stochastic, ATR, ROC)
 */

import {
  registerIndicator,
  type IndicatorFigure,
  type KLineData,
} from "klinecharts";

// ---------------------------------------------------------------------------
// Math (ported from lib/indicators.ts; OhlcvBar shape replaced with KLineData)
// ---------------------------------------------------------------------------

function smaArr(bars: KLineData[], period: number): (number | null)[] {
  const out: (number | null)[] = new Array(bars.length).fill(null);
  if (!Number.isFinite(period) || period < 1) return out;
  let sum = 0;
  for (let i = 0; i < bars.length; i++) {
    sum += bars[i].close;
    if (i >= period) sum -= bars[i - period].close;
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

function emaArr(values: (number | null)[], period: number): (number | null)[] {
  const out: (number | null)[] = new Array(values.length).fill(null);
  if (!Number.isFinite(period) || period < 1) return out;
  const k = 2 / (period + 1);
  // Find first index where we have `period` consecutive non-null values to seed.
  let seedStart = -1;
  for (let i = 0; i + period - 1 < values.length; i++) {
    let ok = true;
    let sum = 0;
    for (let j = 0; j < period; j++) {
      const v = values[i + j];
      if (v == null) {
        ok = false;
        break;
      }
      sum += v;
    }
    if (ok) {
      seedStart = i;
      out[i + period - 1] = sum / period;
      break;
    }
  }
  if (seedStart < 0) return out;
  let prev = out[seedStart + period - 1] as number;
  for (let i = seedStart + period; i < values.length; i++) {
    const v = values[i];
    if (v == null) continue;
    prev = v * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

function emaCloseArr(bars: KLineData[], period: number): (number | null)[] {
  return emaArr(
    bars.map((b) => b.close),
    period,
  );
}

function wmaArr(bars: KLineData[], period: number): (number | null)[] {
  const out: (number | null)[] = new Array(bars.length).fill(null);
  if (!Number.isFinite(period) || period < 1) return out;
  const weightSum = (period * (period + 1)) / 2;
  for (let i = period - 1; i < bars.length; i++) {
    let w = 0;
    for (let j = 0; j < period; j++) {
      w += bars[i - period + 1 + j].close * (j + 1);
    }
    out[i] = w / weightSum;
  }
  return out;
}

function rsiArr(bars: KLineData[], period: number): (number | null)[] {
  const out: (number | null)[] = new Array(bars.length).fill(null);
  if (!Number.isFinite(period) || period < 1 || bars.length < period + 1) return out;
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const change = bars[i].close - bars[i - 1].close;
    if (change >= 0) avgGain += change;
    else avgLoss -= change;
  }
  avgGain /= period;
  avgLoss /= period;
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < bars.length; i++) {
    const change = bars[i].close - bars[i - 1].close;
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    const rs = avgLoss === 0 ? Infinity : avgGain / avgLoss;
    out[i] = Number.isFinite(rs) ? 100 - 100 / (1 + rs) : 100;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Per-indicator results (typed for klinecharts' generic <D>)
// ---------------------------------------------------------------------------

interface SinglePoint {
  value: number | null;
}
interface BollingerPoint {
  up: number | null;
  mid: number | null;
  dn: number | null;
}
interface DonchianPoint {
  up: number | null;
  mid: number | null;
  dn: number | null;
}
interface MacdPoint {
  macd: number | null;
  signal: number | null;
  hist: number | null;
}
interface StochasticPoint {
  k: number | null;
  d: number | null;
}
interface BandLinePoint {
  // `value` is the primary line (Supertrend trailing stop / VWAP); `up`/`dn`
  // are the volatility band envelope around it.
  value: number | null;
  up: number | null;
  dn: number | null;
}
// Golden ± offset[i] on both sides (u{i} = above, d{i} = below), up to
// LADDER_MAX levels. Dynamic keys, so typed as a Record.
type LadderPoint = Record<string, number | null>;
const LADDER_MAX = 6;

// ---------------------------------------------------------------------------
// Registrations
// ---------------------------------------------------------------------------

let registered = false;

export function registerCustomIndicators(): void {
  if (registered) return;
  registered = true;

  // ----- SMA --------------------------------------------------------------
  registerIndicator<SinglePoint, number>({
    name: "SMA",
    shortName: "SMA",
    series: "price",
    calcParams: [20],
    figures: [{ key: "value", title: "SMA: ", type: "line" }],
    calc: (dataList, ind) => {
      const period = Number(ind.calcParams[0]) || 20;
      return smaArr(dataList, period).map((v) => ({ value: v }));
    },
  });

  // ----- EMA --------------------------------------------------------------
  registerIndicator<SinglePoint, number>({
    name: "EMA",
    shortName: "EMA",
    series: "price",
    calcParams: [20],
    figures: [{ key: "value", title: "EMA: ", type: "line" }],
    calc: (dataList, ind) => {
      const period = Number(ind.calcParams[0]) || 20;
      return emaCloseArr(dataList, period).map((v) => ({ value: v }));
    },
  });

  // ----- WMA --------------------------------------------------------------
  registerIndicator<SinglePoint, number>({
    name: "WMA",
    shortName: "WMA",
    series: "price",
    calcParams: [20],
    figures: [{ key: "value", title: "WMA: ", type: "line" }],
    calc: (dataList, ind) => {
      const period = Number(ind.calcParams[0]) || 20;
      return wmaArr(dataList, period).map((v) => ({ value: v }));
    },
  });

  // ----- Bollinger Bands --------------------------------------------------
  registerIndicator<BollingerPoint, number>({
    name: "BOLL",
    shortName: "BOLL",
    series: "price",
    calcParams: [20, 2],
    figures: [
      { key: "up", title: "UP: ", type: "line" } as IndicatorFigure<BollingerPoint>,
      { key: "mid", title: "MID: ", type: "line" } as IndicatorFigure<BollingerPoint>,
      { key: "dn", title: "DN: ", type: "line" } as IndicatorFigure<BollingerPoint>,
    ],
    calc: (dataList, ind) => {
      const period = Number(ind.calcParams[0]) || 20;
      const stdDev = Number(ind.calcParams[1]) || 2;
      const out: BollingerPoint[] = [];
      for (let i = 0; i < dataList.length; i++) {
        if (i < period - 1) {
          out.push({ up: null, mid: null, dn: null });
          continue;
        }
        let sum = 0;
        for (let j = i - period + 1; j <= i; j++) sum += dataList[j].close;
        const mean = sum / period;
        let varSum = 0;
        for (let j = i - period + 1; j <= i; j++) {
          varSum += (dataList[j].close - mean) ** 2;
        }
        const sd = Math.sqrt(varSum / period);
        out.push({
          up: mean + stdDev * sd,
          mid: mean,
          dn: mean - stdDev * sd,
        });
      }
      return out;
    },
  });

  // ----- Donchian Channels ------------------------------------------------
  registerIndicator<DonchianPoint, number>({
    name: "DONCH",
    shortName: "DONCH",
    series: "price",
    calcParams: [20],
    figures: [
      { key: "up", title: "UP: ", type: "line" } as IndicatorFigure<DonchianPoint>,
      { key: "mid", title: "MID: ", type: "line" } as IndicatorFigure<DonchianPoint>,
      { key: "dn", title: "DN: ", type: "line" } as IndicatorFigure<DonchianPoint>,
    ],
    calc: (dataList, ind) => {
      const period = Number(ind.calcParams[0]) || 20;
      const out: DonchianPoint[] = [];
      for (let i = 0; i < dataList.length; i++) {
        if (i < period - 1) {
          out.push({ up: null, mid: null, dn: null });
          continue;
        }
        let hi = -Infinity;
        let lo = Infinity;
        for (let j = i - period + 1; j <= i; j++) {
          if (dataList[j].high > hi) hi = dataList[j].high;
          if (dataList[j].low < lo) lo = dataList[j].low;
        }
        out.push({ up: hi, mid: (hi + lo) / 2, dn: lo });
      }
      return out;
    },
  });

  // ----- RSI --------------------------------------------------------------
  registerIndicator<SinglePoint, number>({
    name: "RSI",
    shortName: "RSI",
    series: "normal",
    calcParams: [14],
    figures: [{ key: "value", title: "RSI: ", type: "line" }],
    calc: (dataList, ind) => {
      const period = Number(ind.calcParams[0]) || 14;
      return rsiArr(dataList, period).map((v) => ({ value: v }));
    },
  });

  // ----- MACD -------------------------------------------------------------
  registerIndicator<MacdPoint, number>({
    name: "MACD",
    shortName: "MACD",
    series: "normal",
    calcParams: [12, 26, 9],
    figures: [
      { key: "macd", title: "MACD: ", type: "line" } as IndicatorFigure<MacdPoint>,
      { key: "signal", title: "SIG: ", type: "line" } as IndicatorFigure<MacdPoint>,
      {
        key: "hist",
        title: "HIST: ",
        type: "bar",
        styles: (params) => {
          const v = params.data.current?.hist ?? 0;
          return { color: v >= 0 ? "#059669" : "#dc2626" };
        },
      } as IndicatorFigure<MacdPoint>,
    ],
    calc: (dataList, ind) => {
      const fast = Number(ind.calcParams[0]) || 12;
      const slow = Number(ind.calcParams[1]) || 26;
      const sig = Number(ind.calcParams[2]) || 9;
      const fastEma = emaCloseArr(dataList, fast);
      const slowEma = emaCloseArr(dataList, slow);
      const macdLine: (number | null)[] = dataList.map((_, i) => {
        const f = fastEma[i];
        const s = slowEma[i];
        return f != null && s != null ? f - s : null;
      });
      const signalLine = emaArr(macdLine, sig);
      return dataList.map((_, i) => {
        const m = macdLine[i];
        const s = signalLine[i];
        return {
          macd: m,
          signal: s,
          hist: m != null && s != null ? m - s : null,
        };
      });
    },
  });

  // ----- Stochastic -------------------------------------------------------
  registerIndicator<StochasticPoint, number>({
    name: "STOCH",
    shortName: "STOCH",
    series: "normal",
    calcParams: [14, 3],
    figures: [
      { key: "k", title: "%K: ", type: "line" } as IndicatorFigure<StochasticPoint>,
      { key: "d", title: "%D: ", type: "line" } as IndicatorFigure<StochasticPoint>,
    ],
    calc: (dataList, ind) => {
      const kPeriod = Number(ind.calcParams[0]) || 14;
      const dPeriod = Number(ind.calcParams[1]) || 3;
      const k: (number | null)[] = new Array(dataList.length).fill(null);
      for (let i = kPeriod - 1; i < dataList.length; i++) {
        let hi = -Infinity;
        let lo = Infinity;
        for (let j = i - kPeriod + 1; j <= i; j++) {
          if (dataList[j].high > hi) hi = dataList[j].high;
          if (dataList[j].low < lo) lo = dataList[j].low;
        }
        const range = hi - lo;
        k[i] = range === 0 ? 50 : ((dataList[i].close - lo) / range) * 100;
      }
      const d = emaArr(k, dPeriod);
      return dataList.map((_, i) => ({ k: k[i], d: d[i] }));
    },
  });

  // ----- ATR --------------------------------------------------------------
  registerIndicator<SinglePoint, number>({
    name: "ATR",
    shortName: "ATR",
    series: "normal",
    calcParams: [14],
    figures: [{ key: "value", title: "ATR: ", type: "line" }],
    calc: (dataList, ind) => {
      const period = Number(ind.calcParams[0]) || 14;
      const tr: (number | null)[] = [null];
      for (let i = 1; i < dataList.length; i++) {
        const prevClose = dataList[i - 1].close;
        tr.push(
          Math.max(
            dataList[i].high - dataList[i].low,
            Math.abs(dataList[i].high - prevClose),
            Math.abs(dataList[i].low - prevClose),
          ),
        );
      }
      const atr = emaArr(tr, period);
      return atr.map((v) => ({ value: v }));
    },
  });

  // ----- ROC --------------------------------------------------------------
  registerIndicator<SinglePoint, number>({
    name: "ROC",
    shortName: "ROC",
    series: "normal",
    calcParams: [10],
    figures: [{ key: "value", title: "ROC: ", type: "line" }],
    calc: (dataList, ind) => {
      const period = Number(ind.calcParams[0]) || 10;
      const out: SinglePoint[] = [];
      for (let i = 0; i < dataList.length; i++) {
        if (i < period) {
          out.push({ value: null });
          continue;
        }
        const prev = dataList[i - period].close;
        out.push({
          value: prev === 0 ? null : ((dataList[i].close - prev) / prev) * 100,
        });
      }
      return out;
    },
  });

  // ----- Supertrend -------------------------------------------------------
  // ATR-banded trailing-stop trend line. calcParams: [atrPeriod, multiplier].
  // ATR is a simple mean of True Range over atrPeriod (matches the Python
  // strategy in strategies/supertrend.py). The line sits below price in an
  // uptrend (the long trailing stop) and above it in a downtrend.
  registerIndicator<SinglePoint, number>({
    name: "SUPERTREND",
    shortName: "ST",
    series: "price",
    calcParams: [10, 3],
    // Just the active trailing stop (the Supertrend line). The raw ATR band
    // envelope is intentionally not drawn — the trailing line already conveys
    // the trade level; the bands were redundant clutter.
    figures: [{ key: "value", title: "ST: ", type: "line" }],
    calc: (dataList, ind) => {
      const atrPeriod = Number(ind.calcParams[0]) || 10;
      const mult = Number(ind.calcParams[1]) || 3;
      const out: SinglePoint[] = dataList.map(() => ({ value: null }));
      const trWindow: number[] = [];
      let prevUpper: number | null = null;
      let prevLower: number | null = null;
      let prevClose: number | null = null;
      let trend: 1 | -1 | null = null;
      for (let i = 0; i < dataList.length; i++) {
        const bar = dataList[i];
        if (prevClose !== null) {
          const tr = Math.max(
            bar.high - bar.low,
            Math.abs(bar.high - prevClose),
            Math.abs(bar.low - prevClose),
          );
          trWindow.push(tr);
          if (trWindow.length > atrPeriod) trWindow.shift();
        }
        if (trWindow.length < atrPeriod) {
          prevClose = bar.close;
          continue;
        }
        const atr = trWindow.reduce((a, b) => a + b, 0) / trWindow.length;
        const hl2 = (bar.high + bar.low) / 2;
        const basicUpper = hl2 + mult * atr;
        const basicLower = hl2 - mult * atr;
        let finalUpper: number;
        let finalLower: number;
        if (prevUpper === null || prevLower === null) {
          finalUpper = basicUpper;
          finalLower = basicLower;
          trend = bar.close >= hl2 ? 1 : -1;
        } else {
          finalUpper =
            prevClose !== null && prevClose < prevUpper
              ? Math.min(basicUpper, prevUpper)
              : basicUpper;
          finalLower =
            prevClose !== null && prevClose > prevLower
              ? Math.max(basicLower, prevLower)
              : basicLower;
          if (trend === -1 && bar.close > prevUpper) trend = 1;
          else if (trend === 1 && bar.close < prevLower) trend = -1;
        }
        out[i] = { value: trend === 1 ? finalLower : finalUpper };
        prevUpper = finalUpper;
        prevLower = finalLower;
        prevClose = bar.close;
      }
      return out;
    },
  });

  // ----- VWAP -------------------------------------------------------------
  // Session volume-weighted average price, reset each IST trading day. No
  // calcParams. Bars carry UTC-ms timestamps; the IST day index is
  // floor((ts + 5.5h) / 24h) so the reset lands at the IST midnight boundary
  // without a timezone library. Bars with volume=0 don't move VWAP.
  // calcParams: [deviationPct] — a fraction (0.005 = 0.5%). When > 0 the
  // ±deviation bands (the vwap_reversion entry triggers) are drawn; 0 hides
  // them (plain VWAP line).
  registerIndicator<BandLinePoint, number>({
    name: "VWAP",
    shortName: "VWAP",
    series: "price",
    calcParams: [0],
    figures: [
      { key: "value", title: "VWAP: ", type: "line" },
      { key: "up", title: "Upper: ", type: "line" },
      { key: "dn", title: "Lower: ", type: "line" },
    ],
    calc: (dataList, ind) => {
      const dev = Number(ind.calcParams[0]) || 0;
      const IST_OFFSET_MS = 19_800_000; // 5h30m
      const DAY_MS = 86_400_000;
      let dayIdx: number | null = null;
      let cumPv = 0;
      let cumVol = 0;
      return dataList.map((b) => {
        const di = Math.floor((b.timestamp + IST_OFFSET_MS) / DAY_MS);
        if (di !== dayIdx) {
          dayIdx = di;
          cumPv = 0;
          cumVol = 0;
        }
        const typical = (b.high + b.low + b.close) / 3;
        const vol = b.volume ?? 0;
        cumPv += typical * vol;
        cumVol += vol;
        const vwap = cumVol > 0 ? cumPv / cumVol : null;
        return {
          value: vwap,
          up: vwap !== null && dev > 0 ? vwap * (1 + dev) : null,
          dn: vwap !== null && dev > 0 ? vwap * (1 - dev) : null,
        };
      });
    },
  });

  // ----- Golden number (per-day horizontal level) -------------------------
  // Plots the Golden Number Breakout strategy's daily golden number as a step
  // line — constant within each IST session, jumping at day boundaries. Values
  // are looked up per bar from a module-level day->value map PriceChart fills
  // via setGoldenMap (klinecharts indicators can't take ad-hoc external data —
  // same approach the old EQUITY pane used).
  registerIndicator<SinglePoint, never>({
    name: "GOLDEN",
    shortName: "Golden",
    series: "price",
    figures: [{ key: "value", title: "Golden: ", type: "line" }],
    calc: (dataList) =>
      dataList.map((b) => ({
        value: goldenByDay.get(istDayKey(b.timestamp)) ?? null,
      })),
  });

  // ----- Golden ladder levels (golden ± offsets, both sides) --------------
  // The Golden Number Breakout entry ladder sits at golden ± ladder_points
  // (above if bullish, below if bearish). Direction flips intraday, so we draw
  // both sides as per-day dotted step-lines — up to LADDER_MAX levels.
  // calcParams: the offsets (0 = unused); golden read from the shared map.
  registerIndicator<LadderPoint, number>({
    name: "GLADDER",
    shortName: "Ladder",
    series: "price",
    calcParams: Array.from({ length: LADDER_MAX }, () => 0),
    figures: Array.from({ length: LADDER_MAX }, (_, i) => [
      { key: `u${i}`, title: "+", type: "line" as const },
      { key: `d${i}`, title: "-", type: "line" as const },
    ]).flat(),
    calc: (dataList, ind) => {
      const offs = Array.from({ length: LADDER_MAX }, (_, i) => {
        const v = Number(ind.calcParams[i]);
        return Number.isFinite(v) && v > 0 ? v : null;
      });
      return dataList.map((b) => {
        const g = goldenByDay.get(istDayKey(b.timestamp)) ?? null;
        const out: LadderPoint = {};
        for (let i = 0; i < LADDER_MAX; i++) {
          const o = offs[i];
          out[`u${i}`] = g !== null && o !== null ? g + o : null;
          out[`d${i}`] = g !== null && o !== null ? g - o : null;
        }
        return out;
      });
    },
  });

  // ----- Value area (VAH / VAL as per-day step lines) ---------------------
  // The opening window's volume-profile value-area bounds, read per bar from
  // the shared day->(vah,val) map PriceChart fills via setValueAreaMap. Drawn
  // whenever a run carries them, so the band the flip measures from is visible
  // rather than inferred.
  registerIndicator<LadderPoint, never>({
    name: "VALUEAREA",
    shortName: "VA",
    series: "price",
    figures: [
      { key: "vah", title: "VAH: ", type: "line" },
      { key: "val", title: "VAL: ", type: "line" },
    ],
    calc: (dataList) =>
      dataList.map((b) => {
        const va = valueAreaByDay.get(istDayKey(b.timestamp));
        return { vah: va ? va.vah : null, val: va ? va.val : null };
      }),
  });

  // ----- Value-area ladder rungs ------------------------------------------
  // With `ladders_on_value_area` the rungs run INWARD from the value-area
  // edges: a bull (call) ladder at VAL + each offset, a bear (put) ladder at
  // VAH - each offset. Direction flips intraday so both sides are drawn, same
  // as GLADDER does for the golden band.
  registerIndicator<LadderPoint, number>({
    name: "VALADDER",
    shortName: "VA ladder",
    series: "price",
    calcParams: Array.from({ length: LADDER_MAX }, () => 0),
    figures: Array.from({ length: LADDER_MAX }, (_, i) => [
      { key: `c${i}`, title: "call ", type: "line" as const },
      { key: `p${i}`, title: "put ", type: "line" as const },
    ]).flat(),
    calc: (dataList, ind) => {
      const offs = Array.from({ length: LADDER_MAX }, (_, i) => {
        const v = Number(ind.calcParams[i]);
        return Number.isFinite(v) && v > 0 ? v : null;
      });
      return dataList.map((b) => {
        const va = valueAreaByDay.get(istDayKey(b.timestamp));
        const out: LadderPoint = {};
        for (let i = 0; i < LADDER_MAX; i++) {
          const o = offs[i];
          // call rungs measure UP from VAL, put rungs DOWN from VAH
          out[`c${i}`] = va && o !== null ? va.val + o : null;
          out[`p${i}`] = va && o !== null ? va.vah - o : null;
        }
        return out;
      });
    },
  });
}

/** IST calendar day ("YYYY-MM-DD") for a UTC-ms timestamp. IST = UTC+5:30, so
 *  the date of (ts + 5h30m) read in UTC is the IST date. Matches the backend's
 *  `date.isoformat()` keys. */
function istDayKey(tsMs: number): string {
  return new Date(tsMs + 19_800_000).toISOString().slice(0, 10);
}

/** day ("YYYY-MM-DD", IST) -> golden number. Owned by the chart instance. */
const goldenByDay: Map<string, number> = new Map();

export function setGoldenMap(entries: Iterable<readonly [string, number]>): void {
  goldenByDay.clear();
  for (const [day, v] of entries) {
    goldenByDay.set(day, v);
  }
}

/** day ("YYYY-MM-DD", IST) -> value-area bounds. Owned by the chart instance,
 *  same pattern as goldenByDay — klinecharts indicators cannot take ad-hoc
 *  external data, so the calc reads a module-level map. */
const valueAreaByDay: Map<string, { vah: number; val: number }> = new Map();

export function setValueAreaMap(
  entries: Iterable<readonly [string, { vah: number; val: number }]>,
): void {
  valueAreaByDay.clear();
  for (const [day, v] of entries) {
    valueAreaByDay.set(day, v);
  }
}
